<script setup>
import { ref, computed, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import Auth from 'dashboard/api/auth';
import Avatar from 'next/avatar/Avatar.vue';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { setColorTheme } from 'dashboard/helper/themeHelper.js';
import {
  buildReportsSections,
  buildCaptainSections,
  buildMarketingSections,
  buildSettingsSections,
} from 'dashboard/components-next/topbar/adminNavSections';

// Rodada mobile F2 (07/09): a barra SUPERIOR morreu no celular — a
// inferior vira o unico chassi: Inicio / CRM / Conversas (centro) /
// Notificacoes / hamburguer. No topo da folha: busca + Brain, e o bloco
// de identidade (perfil, conta, agenda, tema). So < md.
const route = useRoute();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const currentRole = useMapGetter('getCurrentRole');
const isAdministrator = computed(() => currentRole.value === 'administrator');

const currentUser = useMapGetter('getCurrentUser');
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const { currentAccount, accountId } = useAccount();

// Brain/copiloto: mesmo gate do topo desktop
const copilotEnabled = computed(
  () => currentAccount.value?.captain_preferences?.features?.copilot === true
);
const openCopilot = () => {
  emitter.emit(BUS_EVENTS.OPEN_BRAIN_COPILOT);
  // eslint-disable-next-line no-use-before-define
  closeMore();
};

// Tema claro/escuro (mesma mecanica do TopBar desktop)
const isDark = ref(document.body.classList.contains('dark'));
const toggleTheme = () => {
  const newTheme = isDark.value ? 'light' : 'dark';
  LocalStorage.set(LOCAL_STORAGE_KEYS.COLOR_SCHEME, newTheme);
  const isOSOnDarkMode = window.matchMedia(
    '(prefers-color-scheme: dark)'
  ).matches;
  setColorTheme(isOSOnDarkMode);
  isDark.value = document.body.classList.contains('dark');
};

// Troca de conta: mesmas contas do menu de perfil do desktop
const sortedAccounts = computed(() =>
  [...(currentUser.value?.accounts || [])].sort((a, b) =>
    a.name.localeCompare(b.name)
  )
);
const switchAccount = newId => {
  if (newId === accountId.value) return;
  window.location.href = `/app/accounts/${newId}/dashboard`;
};

const logout = () => Auth.logout();

const CONVERSATION_ROUTES = [
  'home',
  'inbox_conversation',
  'conversation_through_mentions',
  'conversation_through_unattended',
  'conversations_through_label',
  'conversations_through_team',
  'conversation_through_inbox',
  'conversations_through_folders',
  'inbox_view',
  'inbox_view_conversation',
];

const tabs = computed(() => [
  {
    name: 'home',
    label: t('SIDEBAR.HOME'),
    icon: 'i-lucide-home',
    to: accountScopedRoute('dashboard_home'),
    activeOn: ['dashboard_home'],
  },
  {
    name: 'crm',
    label: t('SIDEBAR.CRM'),
    icon: 'i-lucide-kanban',
    to: accountScopedRoute('crm_board'),
    activeOn: ['crm_board', 'crm_item_detail', 'crm_list', 'crm_calendar'],
  },
  // Conversas no CENTRO: a acao principal debaixo do polegar
  {
    name: 'conversations',
    label: t('SIDEBAR.CONVERSATIONS'),
    icon: 'i-lucide-message-circle',
    to: accountScopedRoute('home'),
    activeOn: CONVERSATION_ROUTES,
  },
  {
    name: 'notifications',
    label: t('SIDEBAR.NOTIFICATIONS'),
    icon: 'i-lucide-bell',
    to: accountScopedRoute('notifications_index'),
    activeOn: ['notifications_index'],
  },
]);

const isActive = item => item.activeOn.includes(route.name);
const anyTabActive = computed(() => tabs.value.some(isActive));

// Conversa ABERTA no celular = tela cheia, sem barra (padrao WhatsApp):
// sobra espaco pro chat e o teclado nao empurra o menu junto.
const conversationOpen = computed(() =>
  Boolean(route.params.conversation_id || route.params.conversationId)
);

// Badge de nao lidas (mesma fonte do sino de notificacoes)
const notificationMetadata = useMapGetter('notifications/getMeta');
const unreadCount = computed(() => {
  const count = notificationMetadata.value?.unreadCount;
  if (!count) return '';
  return count < 100 ? `${count}` : '99+';
});

const showMore = ref(false);

// Folha "Mais" = a lateral do desktop, na mesma ordem; Administracao
// (o topo do desktop) fecha a lista, so pra admin.
const moreGroups = computed(() => {
  const groups = [
    {
      title: t('SIDEBAR.SECTION_SERVICE'),
      items: [
        {
          label: t('SIDEBAR.CONTACTS'),
          icon: 'i-lucide-contact',
          to: accountScopedRoute('contacts_dashboard_index'),
        },
        {
          label: t('SIDEBAR.COMPANIES'),
          icon: 'i-lucide-building-2',
          to: accountScopedRoute('companies_dashboard_index'),
        },
        {
          label: t('SIDEBAR.TICKETS'),
          icon: 'i-lucide-ticket',
          to: accountScopedRoute('crm_tickets_board'),
        },
        {
          label: t('SIDEBAR.TASKS'),
          icon: 'i-lucide-check-square',
          to: accountScopedRoute('crm_tasks'),
        },
      ],
    },
    {
      title: t('SIDEBAR.SECTION_SALES'),
      items: [
        {
          label: t('SIDEBAR.PRODUCTS'),
          icon: 'i-lucide-package',
          to: accountScopedRoute('crm_products'),
        },
        {
          label: t('SIDEBAR.SEGMENTS'),
          icon: 'i-lucide-pie-chart',
          to: accountScopedRoute('segments_index'),
        },
      ],
    },
    {
      title: t('SIDEBAR.SECTION_AGENT_TOOLS'),
      items: [
        {
          label: t('SIDEBAR.CANNED_RESPONSES'),
          icon: 'i-lucide-message-square-quote',
          to: accountScopedRoute('canned_list'),
        },
        {
          label: t('SIDEBAR.MACROS'),
          icon: 'i-lucide-toy-brick',
          to: accountScopedRoute('macros_wrapper'),
        },
        {
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tags',
          to: accountScopedRoute('labels_list'),
        },
        {
          label: t('SIDEBAR.SCHEDULED_MESSAGES'),
          icon: 'i-lucide-calendar-clock',
          to: accountScopedRoute('scheduled_messages_mine'),
        },
      ],
    },
  ];

  if (isAdministrator.value) {
    groups.push({
      title: t('SIDEBAR.SECTION_ADMIN'),
      admin: true,
      // Mesmos rotulos/icones dos atalhos admin do menu de perfil; tocar
      // ABRE a area (mergulho na folha), como o dropdown do desktop.
      items: [
        {
          label: t('SIDEBAR.REPORTS'),
          icon: 'i-lucide-chart-spline',
          area: 'reports',
        },
        { label: 'Brain', icon: 'i-ph-sparkle-fill', area: 'brain' },
        {
          label: t('TOPBAR.NAV_MARKETING'),
          icon: 'i-lucide-rocket',
          area: 'marketing',
        },
        {
          label: t('SIDEBAR.SETTINGS'),
          icon: 'i-lucide-settings',
          area: 'settings',
        },
      ],
    });
  }
  return groups;
});

// Mergulho de area de admin dentro da folha: mesmas opcoes dos dropdowns
// do topo do desktop, vindas da fonte unica (adminNavSections).
const activeArea = ref(null);

const AREA_BUILDERS = {
  reports: buildReportsSections,
  brain: buildCaptainSections,
  marketing: buildMarketingSections,
  settings: buildSettingsSections,
};

const areaSections = computed(() => {
  if (!activeArea.value) return [];
  return AREA_BUILDERS[activeArea.value.area]({ t, accountScopedRoute });
});

const sheetEl = ref(null);

const scrollSheetTop = () => {
  nextTick(() => {
    if (sheetEl.value) sheetEl.value.scrollTop = 0;
  });
};

const openArea = item => {
  activeArea.value = item;
  scrollSheetTop();
};

const backToRoot = () => {
  activeArea.value = null;
  scrollSheetTop();
};

const closeMore = () => {
  showMore.value = false;
  activeArea.value = null;
};

// Gesto de arrastar pra baixo fecha a folha (so quando a lista esta no
// topo — senao o gesto e rolagem normal).
const dragStartY = ref(null);
const dragOffset = ref(0);

const onTouchStart = event => {
  if (sheetEl.value && sheetEl.value.scrollTop > 0) return;
  dragStartY.value = event.touches[0].clientY;
  dragOffset.value = 0;
};

const onTouchMove = event => {
  if (dragStartY.value === null) return;
  const delta = event.touches[0].clientY - dragStartY.value;
  dragOffset.value = Math.max(0, delta);
  // Sem isto o navegador entende o arrasto como pull-to-refresh (F5)
  if (delta > 0 && event.cancelable) event.preventDefault();
};

const onTouchEnd = () => {
  if (dragOffset.value > 70) closeMore();
  dragStartY.value = null;
  dragOffset.value = 0;
};
</script>

<template>
  <nav
    v-if="!conversationOpen"
    class="md:hidden flex flex-shrink-0 bg-n-solid-1 border-t border-n-weak pb-[env(safe-area-inset-bottom)]"
  >
    <RouterLink
      v-for="tab in tabs"
      :key="tab.name"
      :to="tab.to"
      class="flex-1 flex flex-col items-center gap-0.5 pt-2 pb-1.5 no-underline"
      :class="isActive(tab) ? 'text-woot-500' : 'text-n-slate-10'"
      @click="closeMore"
    >
      <span class="relative">
        <span :class="tab.icon" class="size-5 block" />
        <span
          v-if="tab.name === 'notifications' && unreadCount"
          class="min-h-2 min-w-2 p-0.5 px-1 bg-n-ruby-9 rounded-lg absolute -top-1.5 -right-2.5 grid place-items-center text-[9px] leading-none text-n-ruby-3"
        >
          {{ unreadCount }}
        </span>
      </span>
      <span class="text-[10px] font-medium leading-none">{{ tab.label }}</span>
    </RouterLink>
    <button
      class="flex-1 flex flex-col items-center gap-0.5 pt-2 pb-1.5"
      :class="showMore || !anyTabActive ? 'text-woot-500' : 'text-n-slate-10'"
      @click="showMore = !showMore"
    >
      <span class="i-lucide-menu size-5" />
      <span class="text-[10px] font-medium leading-none">
        {{ t('SIDEBAR.MORE') }}
      </span>
    </button>
  </nav>

  <!-- Folha "Mais" -->
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-200"
      leave-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
    >
      <div
        v-if="showMore"
        class="md:hidden fixed inset-0 z-50 bg-n-alpha-black2"
        @click="closeMore"
      />
    </Transition>
    <Transition
      enter-active-class="transition-transform duration-250 ease-out"
      leave-active-class="transition-transform duration-200 ease-in"
      enter-from-class="translate-y-full"
      leave-to-class="translate-y-full"
    >
      <div
        v-if="showMore"
        ref="sheetEl"
        class="md:hidden fixed inset-x-0 bottom-0 z-50 max-h-[85vh] overflow-y-auto overscroll-contain rounded-t-2xl bg-n-surface-1 pb-[calc(1rem+env(safe-area-inset-bottom))]"
        :class="dragOffset ? '' : 'transition-transform duration-200'"
        :style="
          dragOffset ? { transform: `translateY(${dragOffset}px)` } : undefined
        "
        @touchstart="onTouchStart"
        @touchmove="onTouchMove"
        @touchend="onTouchEnd"
      >
        <div class="mx-auto mt-2.5 h-1 w-9 rounded-full bg-n-slate-6" />

        <Transition
          mode="out-in"
          enter-active-class="transition-all duration-150 ease-out"
          leave-active-class="transition-all duration-100 ease-in"
          enter-from-class="opacity-0 translate-x-3"
          leave-to-class="opacity-0 -translate-x-3"
        >
          <div :key="activeArea ? activeArea.label : 'root'">
            <!-- Nivel 1: o menu completo -->
            <template v-if="!activeArea">
              <!-- Busca + Brain lado a lado -->
              <div class="flex items-center gap-2 px-4 pt-3">
                <RouterLink
                  :to="accountScopedRoute('search')"
                  class="flex flex-1 items-center gap-2.5 rounded-full border border-n-weak bg-n-solid-1 px-4 py-2.5 no-underline text-n-slate-10"
                  @click="closeMore"
                >
                  <span class="i-lucide-search size-4 flex-shrink-0" />
                  <span class="text-sm">{{ t('TOPBAR.SEARCH') }}</span>
                </RouterLink>
                <button
                  v-if="copilotEnabled"
                  class="grid size-10 flex-shrink-0 place-content-center rounded-full brain-face"
                  :title="t('CONVERSATION_SIDEBAR.BRAIN_OPEN_COPILOT')"
                  @click="openCopilot"
                >
                  <span class="i-ph-sparkle-fill size-4 text-white" />
                </button>
              </div>

              <!-- Identidade: perfil / conta / agenda / tema -->
              <div class="flex items-center gap-2 px-4 pt-3">
                <button
                  class="flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-n-weak bg-n-solid-1 px-2.5 py-2 text-left"
                  @click="
                    openArea({ area: 'profile', label: currentUser?.name })
                  "
                >
                  <Avatar
                    :name="
                      currentUser?.available_name || currentUser?.name || ''
                    "
                    :src="currentUser?.avatar_url || ''"
                    :size="28"
                    :status="currentUserAvailability"
                    rounded-full
                  />
                  <span
                    class="min-w-0 flex-1 truncate text-sm font-medium text-n-slate-12"
                  >
                    {{ currentUser?.name }}
                  </span>
                </button>
                <button
                  class="flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-n-weak bg-n-solid-1 px-2.5 py-2 text-left"
                  @click="
                    openArea({ area: 'accounts', label: currentAccount?.name })
                  "
                >
                  <Avatar
                    :name="currentAccount?.name || ''"
                    :src="currentAccount?.avatar_url || ''"
                    :size="28"
                    rounded-full
                  />
                  <span
                    class="min-w-0 flex-1 truncate text-sm font-medium text-n-slate-12"
                  >
                    {{ currentAccount?.name }}
                  </span>
                </button>
                <RouterLink
                  :to="accountScopedRoute('crm_calendar')"
                  class="grid size-11 flex-shrink-0 place-content-center rounded-xl border border-n-weak bg-n-solid-1 text-n-slate-11 no-underline"
                  @click="closeMore"
                >
                  <span class="i-lucide-calendar-days size-5" />
                </RouterLink>
                <button
                  class="grid size-11 flex-shrink-0 place-content-center rounded-xl border border-n-weak bg-n-solid-1 text-n-slate-11"
                  :title="
                    isDark ? t('TOPBAR.LIGHT_MODE') : t('TOPBAR.DARK_MODE')
                  "
                  @click="toggleTheme"
                >
                  <span
                    class="size-5"
                    :class="isDark ? 'i-lucide-sun' : 'i-lucide-moon'"
                  />
                </button>
              </div>
              <div
                v-for="(group, index) in moreGroups"
                :key="index"
                class="px-4"
                :class="group.title ? 'pt-4' : 'pt-3'"
              >
                <span
                  v-if="group.title"
                  class="text-[10px] font-semibold uppercase tracking-[1.2px]"
                  :class="group.admin ? 'text-n-iris-11' : 'text-n-slate-10'"
                >
                  {{ group.title }}
                </span>
                <div class="mt-2 grid grid-cols-2 gap-2">
                  <template v-for="item in group.items" :key="item.label">
                    <button
                      v-if="item.area"
                      class="flex items-center gap-2.5 rounded-xl border border-n-weak bg-n-solid-1 px-3 py-3 text-left text-n-slate-12"
                      @click="openArea(item)"
                    >
                      <span
                        :class="item.icon"
                        class="size-5 flex-shrink-0 text-n-iris-9"
                      />
                      <span class="flex-1 text-sm font-medium">
                        {{ item.label }}
                      </span>
                      <span
                        class="i-lucide-chevron-right size-4 text-n-slate-9"
                      />
                    </button>
                    <RouterLink
                      v-else
                      :to="item.to"
                      class="flex items-center gap-2.5 rounded-xl border border-n-weak bg-n-solid-1 px-3 py-3 no-underline text-n-slate-12"
                      @click="closeMore"
                    >
                      <span
                        :class="item.icon"
                        class="size-5 flex-shrink-0 text-n-slate-11"
                      />
                      <span class="text-sm font-medium">{{ item.label }}</span>
                    </RouterLink>
                  </template>
                </div>
              </div>
            </template>

            <!-- Nivel 2: perfil -->
            <template v-else-if="activeArea.area === 'profile'">
              <button
                class="sticky top-0 z-10 flex w-full items-center gap-2 bg-n-surface-1 px-4 py-3 text-n-slate-12 border-b border-n-weak"
                @click="backToRoot"
              >
                <span class="i-lucide-chevron-left size-5 text-n-iris-9" />
                <span class="text-base font-semibold">{{
                  activeArea.label
                }}</span>
              </button>
              <div class="flex flex-col gap-1.5 px-4 pt-3">
                <RouterLink
                  :to="{
                    name: 'profile_settings_index',
                    params: { accountId },
                  }"
                  class="flex items-center gap-2.5 rounded-xl border border-n-weak bg-n-solid-1 px-3 py-3 no-underline text-n-slate-12"
                  @click="closeMore"
                >
                  <span
                    class="i-lucide-user size-5 flex-shrink-0 text-n-slate-11"
                  />
                  <span class="text-sm font-medium">
                    {{ t('SIDEBAR_ITEMS.PROFILE_SETTINGS') }}
                  </span>
                </RouterLink>
                <button
                  class="flex items-center gap-2.5 rounded-xl border border-n-ruby-6 bg-n-solid-1 px-3 py-3 text-left text-n-ruby-11"
                  @click="logout"
                >
                  <span class="i-lucide-log-out size-5 flex-shrink-0" />
                  <span class="text-sm font-medium">
                    {{ t('SIDEBAR_ITEMS.LOGOUT') }}
                  </span>
                </button>
              </div>
            </template>

            <!-- Nivel 2: trocar de conta -->
            <template v-else-if="activeArea.area === 'accounts'">
              <button
                class="sticky top-0 z-10 flex w-full items-center gap-2 bg-n-surface-1 px-4 py-3 text-n-slate-12 border-b border-n-weak"
                @click="backToRoot"
              >
                <span class="i-lucide-chevron-left size-5 text-n-iris-9" />
                <span class="text-base font-semibold">{{
                  activeArea.label
                }}</span>
              </button>
              <div class="flex flex-col gap-1.5 px-4 pt-3">
                <button
                  v-for="account in sortedAccounts"
                  :key="account.id"
                  class="flex items-center gap-2.5 rounded-xl border px-3 py-3 text-left text-n-slate-12"
                  :class="
                    account.id === accountId
                      ? 'border-n-iris-8 bg-n-iris-3'
                      : 'border-n-weak bg-n-solid-1'
                  "
                  @click="switchAccount(account.id)"
                >
                  <Avatar
                    :name="account.name"
                    :src="account.avatar_url || ''"
                    :size="24"
                    rounded-full
                  />
                  <span class="min-w-0 flex-1 truncate text-sm font-medium">
                    {{ account.name }}
                  </span>
                  <span
                    v-if="account.id === accountId"
                    class="i-lucide-check size-4 text-n-iris-9"
                  />
                </button>
              </div>
            </template>

            <!-- Nivel 2: area de admin aberta (mesmas opcoes do topo desktop) -->
            <template v-else>
              <!-- Voltar STICKY: visivel mesmo com a lista rolada -->
              <button
                class="sticky top-0 z-10 flex w-full items-center gap-2 bg-n-surface-1 px-4 py-3 text-n-slate-12 border-b border-n-weak"
                @click="backToRoot"
              >
                <span class="i-lucide-chevron-left size-5 text-n-iris-9" />
                <span class="text-base font-semibold">{{
                  activeArea.label
                }}</span>
              </button>
              <div
                v-for="section in areaSections"
                :key="section.title"
                class="px-4 pt-3"
              >
                <span
                  v-if="section.title"
                  class="text-[10px] font-semibold uppercase tracking-[1.2px] text-n-slate-10"
                >
                  {{ section.title }}
                </span>
                <div class="mt-2 flex flex-col gap-1.5">
                  <RouterLink
                    v-for="item in section.items"
                    :key="item.label"
                    :to="item.link"
                    class="flex items-center gap-2.5 rounded-xl border border-n-weak bg-n-solid-1 px-3 py-2.5 no-underline text-n-slate-12"
                    @click="closeMore"
                  >
                    <span
                      :class="item.icon"
                      class="size-4 flex-shrink-0 text-n-slate-11"
                    />
                    <span class="text-sm font-medium">{{ item.label }}</span>
                  </RouterLink>
                </div>
              </div>
            </template>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>
